import { useCallback, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { devClubMarkSrc } from '@/lib/devclub-verde';

interface DevClubLogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  animated?: boolean;
  /**
   * De tempos em tempos o logo "roda código": o símbolo vira bits que
   * embaralham e se decifram célula por célula até voltar ao desenho, e o nome
   * embaralha em caracteres de código e se resolve. Também roda no hover.
   */
  vivo?: boolean;
}

/**
 * O símbolo do DevClub é uma grade 13×13 (1 = módulo aceso), lida do
 * /logo.png e idêntica à da abertura da LP do DevClub. Desenhado em linha, em
 * unidades de módulo com 1 módulo de margem (viewBox -1 -1 15 15), no verde de
 * --devclub-verde e com fundo transparente: o mesmo desenho parado e
 * embaralhando, sem troca de cor.
 */
export const PADRAO =
  '1110101110101101000000000111101111111010000000000010101110001100110100101001011010010100001001001010000110111000111010000000000000100001001011101001001001011001000010111';
/** Os mesmos caracteres do terminal da abertura da LP. */
export const CARACTERES = '01<>/{}';
const NOME = [
  { letras: 'Grupo', classe: '' },
  { letras: 'Club', classe: 'text-primary' },
];
const INTERVALO = 10000;
export const DURACAO = 1.8;
/** Os bits trocam em degraus (~15 por segundo), com cara de máquina. */
export const DEGRAU = 0.066;

const sorteia = () => CARACTERES[(Math.random() * CARACTERES.length) | 0];

function useCodigoNoLogo(vivo: boolean) {
  const raiz = useRef<HTMLDivElement>(null);
  const simbolo = useRef<SVGSVGElement>(null);
  const nome = useRef<HTMLSpanElement>(null);
  const quadro = useRef<number>();
  const rodando = useRef(false);

  const rodar = useCallback(() => {
    const svg = simbolo.current;
    if (!vivo || rodando.current || !svg) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    rodando.current = true;

    const celulas = Array.from(
      svg.querySelectorAll<SVGRectElement>('[data-k]'),
    );
    const caixa = nome.current;
    const letras = caixa
      ? Array.from(caixa.querySelectorAll<HTMLSpanElement>('[data-letra]'))
      : [];
    // A largura do nome fica presa enquanto embaralha: nada ao lado se mexe.
    if (caixa) caixa.style.width = `${caixa.getBoundingClientRect().width}px`;

    // Uma onda na diagonal: cada célula entra no embaralho e depois assenta no
    // desenho na mesma ordem, com um pouco de acaso para a frente não ser reta.
    const diagonal = (k: number) => ((k % 13) + ((k / 13) | 0)) / 24;
    const entra = celulas.map(
      (_, k) => diagonal(k) * 0.35 + Math.random() * 0.1,
    );
    const assenta = celulas.map(
      (_, k) => 0.95 + diagonal(k) * 0.6 + Math.random() * 0.15,
    );
    const letraEntra = letras.map((_, i) => i * 0.03);
    const letraAssenta = letras.map((_, i) => 0.8 + i * 0.09);

    // a linha verde do topo da lateral anda junto (CSS: .linha-do-logo)
    raiz.current?.setAttribute('data-logo-rodando', '');
    const t0 = performance.now();
    let ultimo = -1;
    const passo = (agora: number) => {
      const s = (agora - t0) / 1000;
      const degrau = Math.floor(s / DEGRAU);
      if (degrau !== ultimo) {
        ultimo = degrau;
        celulas.forEach((c, k) => {
          const noDesenho = s < entra[k] || s >= assenta[k];
          const ligada = noDesenho ? PADRAO[k] === '1' : Math.random() < 0.5;
          c.style.opacity = ligada ? '1' : '0';
        });
        letras.forEach((l, i) => {
          const embaralhando = s >= letraEntra[i] && s < letraAssenta[i];
          l.textContent = embaralhando ? sorteia() : (l.dataset.letra ?? '');
          if (embaralhando) l.dataset.codigo = '';
          else delete l.dataset.codigo;
        });
      }
      if (s < DURACAO) {
        quadro.current = requestAnimationFrame(passo);
        return;
      }
      // repouso garantido mesmo se a aba pulou quadros
      celulas.forEach((c, k) => {
        c.style.opacity = PADRAO[k] === '1' ? '1' : '0';
      });
      raiz.current?.removeAttribute('data-logo-rodando');
      if (caixa) caixa.style.width = '';
      rodando.current = false;
    };
    quadro.current = requestAnimationFrame(passo);
  }, [vivo]);

  useEffect(() => {
    if (!vivo) return;
    const primeira = window.setTimeout(rodar, 1500);
    const repete = window.setInterval(() => {
      if (!document.hidden) rodar();
    }, INTERVALO);
    return () => {
      window.clearTimeout(primeira);
      window.clearInterval(repete);
      if (quadro.current) cancelAnimationFrame(quadro.current);
      rodando.current = false;
      raiz.current?.removeAttribute('data-logo-rodando');
    };
  }, [vivo, rodar]);

  return { raiz, simbolo, nome, rodar };
}

export function DevClubLogo({
  className,
  size = 'md',
  showText = true,
  animated = false,
  vivo = false,
}: DevClubLogoProps) {
  const sizes = {
    sm: { icon: 28, text: 'text-lg' },
    md: { icon: 36, text: 'text-xl' },
    lg: { icon: 44, text: 'text-2xl' },
    xl: { icon: 52, text: 'text-3xl' },
  };

  const { icon, text } = sizes[size];
  const { raiz, simbolo, nome, rodar } = useCodigoNoLogo(vivo);

  return (
    <div
      ref={raiz}
      className={cn('flex items-center gap-3', className)}
      onMouseEnter={vivo ? rodar : undefined}
    >
      <div
        className={cn('relative shrink-0', animated && 'animate-pulse-glow')}
        style={{ width: icon, height: icon }}
      >
        {/* crispEdges: módulos vizinhos encostam sem fiapo entre eles */}
        <svg
          ref={simbolo}
          viewBox="-1 -1 15 15"
          shapeRendering="crispEdges"
          className="block h-full w-full"
          style={{ fill: 'var(--devclub-verde)' }}
          role={showText ? undefined : 'img'}
          aria-label={showText ? undefined : 'DevClub'}
          aria-hidden={showText || undefined}
          focusable="false"
        >
          {Array.from(PADRAO, (bit, k) => (
            <rect
              key={k}
              data-k={k}
              x={k % 13}
              y={Math.floor(k / 13)}
              width={1}
              height={1}
              style={{ opacity: bit === '1' ? 1 : 0 }}
            />
          ))}
        </svg>
      </div>

      {showText && (
        <span
          ref={nome}
          className={cn(
            'inline-block whitespace-nowrap font-bold tracking-tight',
            text,
            animated ? 'gradient-devclub-text' : 'text-foreground',
          )}
        >
          {vivo ? (
            <>
              <span className="sr-only">GrupoClub</span>
              <span aria-hidden="true">
                {NOME.map(({ letras, classe }) => (
                  <span key={letras} className={classe}>
                    {Array.from(letras, (letra, i) => (
                      <span key={i} data-letra={letra} className="letra-logo">
                        {letra}
                      </span>
                    ))}
                  </span>
                ))}
              </span>
            </>
          ) : (
            <>
              Grupo<span className="text-primary">Club</span>
            </>
          )}
        </span>
      )}
    </div>
  );
}

// Simple icon-only version
export function DevClubIcon({
  className,
  size = 32,
}: {
  className?: string;
  size?: number;
}) {
  return (
    <div
      className={cn('shrink-0', className)}
      style={{ width: size, height: size }}
    >
      <img
        src={devClubMarkSrc()}
        alt="DevClub"
        width={size}
        height={size}
        className="block h-full w-full object-contain"
      />
    </div>
  );
}
